"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Loader2, Plus, CheckCircle2, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import type { iBrand, IBrandQuoteThemeConfig } from "@/@sdk/brands/brand-model";
import { useBrandById, useBrands } from "@/hooks/use-brands";
import { selectOrganization } from "@/store/modules/workspace/workspace.selectors";
import { LoadAllBrands } from "@/store/modules/brands/brands.helpers";
import { useStore } from "react-redux";
import type { iStore } from "@/store/store.model";
import {
  BrandBasicsForm,
  buildBrandBasicsPrefillFromWorkspace,
  type BrandBasicsFormValues,
} from "../brand-basics-form";
import {
  BrandDetailsForm,
  brandToDetailsValues,
  type BrandDetailsFormValues,
} from "../brand-details-form";
import { QuoteThemeSectionContent } from "@/components/manage-workspace-modal/sections/quote-theme-section";
import { getEffectiveQuoteThemeConfig } from "@/components/quotes/quotes-template/quote-theme/quote-theme-presets";
import {
  BrandEmailTemplatePreviewButton,
  type BrandEmailTemplatePreviewType,
} from "@/components/manage-workspace-modal/sections/brand-email-template-preview-button";
import {
  BrandChecklistContent,
  type BrandOnboardingChecklistItem,
} from "@/components/manage-workspace-modal/sections/brand-checklist-content";
import {
  BrandDefaultLedgerLinks,
  hasBrandDefaultLedgers,
} from "@/components/accounting/default-ledger-links";

function prepareQuoteThemeConfigForSave(
  config: IBrandQuoteThemeConfig,
  brandPrimaryColor?: string
): IBrandQuoteThemeConfig {
  const primary =
    config.colorPalette.presetId === "brand-primary" && brandPrimaryColor
      ? brandPrimaryColor
      : config.colorPalette.primary;

  const bodyFont =
    config.fontSchema === "SINGLE_FONT"
      ? config.fontGroup.headingFont
      : config.fontGroup.bodyFont;

  return {
    ...config,
    colorPalette: { ...config.colorPalette, primary },
    fontGroup: { ...config.fontGroup, bodyFont },
  };
}

function buildDetailsPayload(data: BrandDetailsFormValues): Partial<iBrand> {
  const postal = data.postalAddress;
  const hasPostal = Boolean(
    postal.line1?.trim() ||
      postal.line2?.trim() ||
      postal.city?.trim() ||
      postal.state?.trim() ||
      postal.postalCode?.trim()
  );

  return {
    legalName: data.legalName?.trim() || undefined,
    atolNumber: data.atolNumber?.trim() || undefined,
    termsAndConditionsUrl: data.termsAndConditionsUrl?.trim() || undefined,
    bookingConditionsUrl: data.bookingConditionsUrl?.trim() || undefined,
    postalAddress: hasPostal
      ? {
          line1: postal.line1?.trim() || undefined,
          line2: postal.line2?.trim() || undefined,
          city: postal.city?.trim() || undefined,
          state: postal.state?.trim() || undefined,
          postalCode: postal.postalCode?.trim() || undefined,
        }
      : undefined,
  };
}

const EMAIL_REVIEW_ITEMS: Array<{
  checklistId: string;
  templateType: BrandEmailTemplatePreviewType;
  label: string;
}> = [
  {
    checklistId: "booking-confirmation-email",
    templateType: "booking-confirmation",
    label: "Booking confirmation",
  },
  {
    checklistId: "payment-reminder-email",
    templateType: "payment-reminder",
    label: "Payment reminder",
  },
  {
    checklistId: "voucher-email",
    templateType: "voucher",
    label: "Voucher email",
  },
  {
    checklistId: "customer-receipt",
    templateType: "customer-receipt",
    label: "Customer receipt",
  },
];

export function BrandBasicsStep({
  brandId,
  singleBrandMode,
  prefillFromWorkspace,
  onSaved,
}: {
  brandId?: string;
  singleBrandMode?: boolean;
  prefillFromWorkspace?: boolean;
  onSaved: (brandId: string) => void;
}) {
  const organization = useSelector(selectOrganization);
  const brand = useBrandById(brandId);
  const store = useStore<iStore>();
  const { retry: retryBrands } = useBrands();

  const defaultValues = useMemo<BrandBasicsFormValues>(() => {
    if (brand) {
      return {
        name: brand.name ?? "",
        homeCurrency: brand.homeCurrency ?? "GBP",
        website: brand.website ?? "",
        country: brand.country ?? "",
        timezone: brand.timezone ?? "",
        primaryColor: brand.primaryColor ?? "#000000",
        logo: brand.logo ?? "",
      };
    }
    if (prefillFromWorkspace || singleBrandMode) {
      return buildBrandBasicsPrefillFromWorkspace(organization);
    }
    return buildBrandBasicsPrefillFromWorkspace(null);
  }, [brand, organization, prefillFromWorkspace, singleBrandMode]);

  const { doAction: createBrand, isProcessing: isCreating } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => (payload: Record<string, unknown>) =>
          sdk.brands.create(payload),
        throwError: true,
      }),
      []
    );

  const { doAction: editBrand, isProcessing: isUpdating } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => (edits: Partial<iBrand>) =>
          sdk.brands.editById(brandId!, edits),
        throwError: true,
      }),
      [brandId]
    );

  const handleSubmit = useCallback(
    async (values: BrandBasicsFormValues) => {
      if (brandId) {
        await editBrand({
          name: values.name,
          website: values.website || undefined,
          country: values.country || undefined,
          timezone: values.timezone || undefined,
          primaryColor: values.primaryColor,
          logo: values.logo || undefined,
        });
        await retryBrands();
        onSaved(brandId);
        return;
      }

      const created = await createBrand({
        name: values.name,
        homeCurrency: values.homeCurrency,
        website: values.website || undefined,
        country: values.country || undefined,
        timezone: values.timezone || undefined,
        primaryColor: values.primaryColor,
        logo: values.logo || undefined,
        isDefault: singleBrandMode,
      });
      if (!created?.id) {
        throw new Error("Failed to create brand");
      }
      await LoadAllBrands(store, true);
      onSaved(created.id);
    },
    [brandId, createBrand, editBrand, onSaved, retryBrands, singleBrandMode, store]
  );

  const isProcessing = isCreating || isUpdating;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {singleBrandMode
          ? "Confirm your brand identity. We prefilled this from your workspace details."
          : "Enter the basics for this brand."}
      </p>
      <BrandBasicsForm
        organizationId={organization?.id}
        defaultValues={defaultValues}
        homeCurrencyLocked={Boolean(brandId)}
        mode="wizard"
        isSubmitting={isProcessing}
        onSubmit={handleSubmit}
      />
      <div className="flex justify-end border-t pt-4">
        <Button type="submit" form="brand-basics-form" disabled={isProcessing}>
          {isProcessing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save and continue
        </Button>
      </div>
    </div>
  );
}

export function BrandDetailsStep({
  brandId,
  onSaved,
}: {
  brandId: string;
  onSaved: () => void;
}) {
  const brand = useBrandById(brandId);
  const { retry: retryBrands } = useBrands();
  const defaultValues = useMemo(
    () => brandToDetailsValues(brand),
    [brand]
  );

  const { doAction: editBrand, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (edits: Partial<iBrand>) =>
        sdk.brands.editById(brandId, edits),
      throwError: true,
    }),
    [brandId]
  );

  const handleSubmit = useCallback(
    async (values: BrandDetailsFormValues) => {
      await editBrand(buildDetailsPayload(values));
      await retryBrands();
      onSaved();
    },
    [editBrand, onSaved, retryBrands]
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Legal details appear on quotes, receipts, and booking emails.
      </p>
      <BrandDetailsForm defaultValues={defaultValues} onSubmit={handleSubmit} />
      {brand && hasBrandDefaultLedgers(brand.defaultLedgerAccounts) ? (
        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="mb-2 text-sm font-medium">Default ledgers</p>
          <BrandDefaultLedgerLinks
            defaultLedgerAccounts={brand.defaultLedgerAccounts}
          />
        </div>
      ) : null}
      <div className="flex justify-end border-t pt-4">
        <Button type="submit" form="brand-details-form" disabled={isProcessing}>
          {isProcessing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save and continue
        </Button>
      </div>
    </div>
  );
}

export function BrandQuoteExperienceStep({
  brandId,
  onSaved,
  onSkip,
}: {
  brandId: string;
  onSaved: () => void;
  onSkip: () => void;
}) {
  const brand = useBrandById(brandId);
  const { retry: retryBrands } = useBrands();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const initialConfig = useMemo(
    () =>
      getEffectiveQuoteThemeConfig(
        brand?.quoteThemeConfig,
        brand?.primaryColor
      ),
    [brand?.quoteThemeConfig, brand?.primaryColor]
  );

  const [draftConfig, setDraftConfig] =
    useState<IBrandQuoteThemeConfig>(initialConfig);

  useEffect(() => {
    setDraftConfig(initialConfig);
  }, [initialConfig]);

  const { doAction: editBrand, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (edits: Partial<iBrand>) =>
        sdk.brands.editById(brandId, edits),
      throwError: true,
    }),
    [brandId]
  );

  const updateConfig = useCallback((patch: Partial<IBrandQuoteThemeConfig>) => {
    setDraftConfig((current) => {
      const next = { ...current, ...patch };
      if (patch.fontSchema === "SINGLE_FONT") {
        next.fontGroup = {
          ...next.fontGroup,
          bodyFont: next.fontGroup.headingFont,
        };
      }
      return next;
    });
  }, []);

  const previewBrand = useMemo(
    (): iBrand | undefined =>
      brand ? { ...brand, quoteThemeConfig: draftConfig } : undefined,
    [brand, draftConfig]
  );

  const handleSave = useCallback(async () => {
    const quoteThemeConfig = prepareQuoteThemeConfigForSave(
      draftConfig,
      brand?.primaryColor
    );
    await editBrand({ quoteThemeConfig });
    await retryBrands();
    onSaved();
  }, [brand?.primaryColor, draftConfig, editBrand, onSaved, retryBrands]);

  return (
    <div className="flex min-h-[65vh] flex-col space-y-4">
      <p className="shrink-0 text-sm text-muted-foreground">
        Choose how your quotes look to customers. You can refine this later in
        Manage Workspace.
      </p>
      <div className="min-h-0 flex-1">
        <QuoteThemeSectionContent
          brandId={brandId}
          brand={brand}
          draftConfig={draftConfig}
          previewBrand={previewBrand}
          advancedOpen={advancedOpen}
          onAdvancedOpenChange={setAdvancedOpen}
          onUpdateConfig={updateConfig}
          showPreview
          layout="fullscreen"
        />
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onSkip}>
          Skip for now
        </Button>
        <Button type="button" onClick={() => void handleSave()} disabled={isProcessing}>
          {isProcessing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save and continue
        </Button>
      </div>
    </div>
  );
}

export function BrandEmailTemplatesStep({
  brandId,
  onContinue,
}: {
  brandId: string;
  onContinue: () => void;
}) {
  const store = useStore<iStore>();
  const [reviewed, setReviewed] = useState<Record<string, boolean>>({});

  const { doAction: loadChecklist, isProcessing: isLoadingChecklist } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => () => sdk.brands.getOnboardingChecklist(brandId),
      }),
      [brandId]
    );

  const [checklistItems, setChecklistItems] = useState<
    BrandOnboardingChecklistItem[]
  >([]);

  useEffect(() => {
    void loadChecklist().then((data) => {
      if (!data?.items) return;
      setChecklistItems(data.items);
      const initial: Record<string, boolean> = {};
      for (const item of data.items) {
        if (item.isReviewed) initial[item.id] = true;
      }
      setReviewed(initial);
    });
  }, [brandId, loadChecklist]);

  const { doAction: updateChecklistItem, isProcessing: isUpdating } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => (itemId: string, isReviewed: boolean) =>
          sdk.brands.updateOnboardingChecklistItem(brandId, itemId, {
            isReviewed,
          }),
        onSuccess: () => LoadAllBrands(store, true),
      }),
      [brandId, store]
    );

  const handleReviewToggle = useCallback(
    async (itemId: string, checked: boolean) => {
      setReviewed((prev) => ({ ...prev, [itemId]: checked }));
      await updateChecklistItem(itemId, checked);
    },
    [updateChecklistItem]
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Preview your booking emails. Default templates were applied when the
        brand was created — mark each as reviewed when you&apos;re happy.
      </p>

      {isLoadingChecklist ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading templates…
        </div>
      ) : (
        <div className="space-y-3">
          {EMAIL_REVIEW_ITEMS.map((item) => {
            const checklistItem = checklistItems.find(
              (entry) => entry.id === item.checklistId
            );
            const isChecked = reviewed[item.checklistId] ?? false;

            return (
              <div
                key={item.checklistId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`review-${item.checklistId}`}
                    checked={isChecked}
                    disabled={isUpdating}
                    onCheckedChange={(value) =>
                      void handleReviewToggle(item.checklistId, value === true)
                    }
                  />
                  <label
                    htmlFor={`review-${item.checklistId}`}
                    className="text-sm font-medium"
                  >
                    {item.label}
                  </label>
                  {checklistItem?.isComplete && isChecked ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : null}
                </div>
                <BrandEmailTemplatePreviewButton
                  brandId={brandId}
                  templateType={item.templateType}
                  variant="outline"
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end border-t pt-4">
        <Button type="button" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
}

export function BrandSummaryStep({
  brandId,
  onComplete,
}: {
  brandId: string;
  onComplete: () => void;
}) {
  const brand = useBrandById(brandId);
  const [checklist, setChecklist] = useState<{
    items: BrandOnboardingChecklistItem[];
    completedCount: number;
    totalCount: number;
  } | null>(null);

  const incompleteItems = useMemo(
    () => (checklist?.items ?? []).filter((item) => !item.isComplete),
    [checklist?.items]
  );

  const { doAction: loadChecklist } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => () => sdk.brands.getOnboardingChecklist(brandId),
    }),
    [brandId]
  );

  useEffect(() => {
    void loadChecklist().then((data) => {
      if (data) setChecklist(data);
    });
  }, [brandId, loadChecklist]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/30 p-4">
        <h3 className="text-base font-semibold">
          {brand?.name ?? "Your brand"} is ready to use
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Core brand setup is complete. You can finish remaining checklist items
          anytime in Manage Workspace.
        </p>
      </div>

      {checklist ? (
        <BrandChecklistContent
          brandId={brandId}
          items={checklist.items}
          completedCount={checklist.completedCount}
          totalCount={checklist.totalCount}
          showProgress
        />
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading checklist…
        </div>
      )}

      {incompleteItems.length > 0 ? (
        <div className="space-y-2 rounded-lg border border-dashed p-4">
          <p className="text-sm font-medium">Finish later in Manage Workspace</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {incompleteItems.slice(0, 6).map((item) => (
              <li key={item.id}>• {item.label}</li>
            ))}
            {incompleteItems.length > 6 ? (
              <li>• and {incompleteItems.length - 6} more</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-end border-t pt-4">
        <Button type="button" onClick={onComplete}>
          Complete brand setup
        </Button>
      </div>
    </div>
  );
}

export function BrandLoopList({
  completedBrandIds,
  singleBrandMode = false,
  needsInitialSetup = false,
  onAddBrand,
  onEditBrand,
}: {
  completedBrandIds: string[];
  singleBrandMode?: boolean;
  needsInitialSetup?: boolean;
  onAddBrand: () => void;
  onEditBrand: (brandId: string) => void;
}) {
  const { brands } = useBrands();
  const completedBrands = brands.filter((b) =>
    completedBrandIds.includes(b.id!)
  );

  const addButtonLabel = needsInitialSetup
    ? "Set up your brand"
    : singleBrandMode
      ? "Edit brand setup"
      : "Add brand";

  const showAddBrandButton =
    needsInitialSetup || !singleBrandMode || completedBrands.length === 0;

  const handleAddBrandClick = () => {
    if (
      singleBrandMode &&
      completedBrands.length === 1 &&
      completedBrands[0]?.id
    ) {
      onEditBrand(completedBrands[0].id);
      return;
    }
    onAddBrand();
  };

  return (
    <div className="flex flex-col items-center py-4 text-center">
      {needsInitialSetup ? (
        <div className="mb-8 max-w-md space-y-3">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Palette className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold">Set up your brand</h3>
          <p className="text-sm text-muted-foreground">
            Your brand is how customers see your quotes, emails, and booking
            confirmations. We&apos;ll guide you through the essentials.
          </p>
        </div>
      ) : (
        <div className="mb-6 w-full max-w-lg space-y-4 text-left">
          <p className="text-center text-sm text-muted-foreground">
            {singleBrandMode
              ? "Your brand identity powers customer-facing quotes and emails."
              : "Add one or more brands for your agency. Each brand has its own quotes, emails, and customer-facing identity."}
          </p>

          {completedBrands.length > 0 ? (
            <ul className="space-y-2">
              {completedBrands.map((brand) => (
                <li
                  key={brand.id}
                  className="flex items-center justify-between rounded-lg border bg-card p-3"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                    <span className="font-medium">{brand.name}</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onEditBrand(brand.id!)}
                  >
                    Edit setup
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {showAddBrandButton ? (
        <Button type="button" size="lg" onClick={handleAddBrandClick}>
          {!needsInitialSetup ? <Plus className="mr-2 h-4 w-4" /> : null}
          {addButtonLabel}
        </Button>
      ) : null}
    </div>
  );
}
